import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Dropdown } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { fetchWasteCodes } from '../../store/actions/wasteCode';
import { wasteTypes } from '../../utils/constants/wasteTypes';

// Zdieľaný výber kódu odpadu (vyhľadávanie + zoskupenie podľa druhu odpadu).
// Používa sa v evidenčných listoch aj v prevádzkovom denníku.
// Menu sa vykresľuje portálom do <body>, aby ho neorezával overflow žiadneho rodiča.
const WasteCodeSelect = ({
    value,
    onSelect,
    label,
    placeholder = 'Vyberte alebo zadajte kód odpadu:',
    isInvalid,
    errorMessage,
    onBlur,
    className = 'mb-3'
}) => {
    const dispatch = useDispatch();
    const wasteCodes = useSelector(state => state.wasteCodes.data);

    const [query, setQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredWaste, setFilteredWaste] = useState([]);
    const [menuStyle, setMenuStyle] = useState(null);

    const wrapperRef = useRef(null);
    const inputWrapperRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        dispatch(fetchWasteCodes());
    }, [dispatch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current?.contains(event.target)) return;
            if (menuRef.current?.contains(event.target)) return;
            setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter waste codes
    useEffect(() => {
        if (query === '') {
            setFilteredWaste(wasteCodes);
        } else {
            const normalizedQuery = query.replace(/\s/g, '').toLowerCase();
            setFilteredWaste(
                wasteCodes.filter((waste) =>
                    waste.wasteName.toLowerCase().includes(query.toLowerCase()) ||
                    waste.wasteCode.replace(/\s/g, '').toLowerCase().includes(normalizedQuery)
                )
            );
        }
    }, [query, wasteCodes]);

    // Keep the input text in sync with the externally controlled value
    useEffect(() => {
        if (value && wasteCodes.length > 0) {
            const selectedWaste = wasteCodes.find((waste) => waste._id === value);
            if (selectedWaste) {
                setQuery(`${selectedWaste.wasteCode} - ${selectedWaste.wasteName}`);
            }
        } else if (!value) {
            setQuery('');
        }
    }, [value, wasteCodes]);

    // Position the portal-rendered menu against the input, so it works regardless of ancestor overflow.
    // Re-measured every frame (not just once, or on scroll/resize) while the dropdown is open, since
    // the input can still be moving via a CSS transition (e.g. a modal sliding in) with no scroll or
    // resize event of its own - a one-shot measurement can catch it mid-animation and freeze the menu
    // at the wrong position for good.
    useLayoutEffect(() => {
        if (!showDropdown) return;
        let frameId;
        const updatePosition = () => {
            if (!inputWrapperRef.current) return;
            const rect = inputWrapperRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 2,
                left: rect.left,
                width: rect.width,
                zIndex: 3000
            });
            frameId = requestAnimationFrame(updatePosition);
        };
        updatePosition();
        return () => cancelAnimationFrame(frameId);
    }, [showDropdown]);

    // Skupinová kategorizácia odpadu podľa typu
    const groupedWaste = filteredWaste.reduce((acc, waste) => {
        const type = waste.wasteType;
        if (!acc[type]) {
            acc[type] = [];
        }
        acc[type].push(waste);
        return acc;
    }, {});

    const handleSelect = (waste) => {
        setQuery(`${waste.wasteCode} - ${waste.wasteName}`);
        setShowDropdown(false);
        onSelect(waste);
    };

    const handleClear = () => {
        setQuery('');
        setShowDropdown(false);
        onSelect(null);
    };

    return (
        <Form.Group className={className} ref={wrapperRef}>
            {label && <Form.Label>{label}</Form.Label>}
            <div ref={inputWrapperRef} style={{ position: 'relative' }}>
                <Form.Control
                    type="text"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setShowDropdown(true);
                        if (!e.target.value) onSelect(null);
                    }}
                    onFocus={() => {
                        setShowDropdown(true);
                        setFilteredWaste(wasteCodes);
                    }}
                    onBlur={onBlur}
                    isInvalid={isInvalid}
                />

                <Form.Control.Feedback type="invalid">
                    {errorMessage}
                </Form.Control.Feedback>

                {query && (
                    <span
                        onClick={handleClear}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            color: '#888'
                        }}
                    >
                        <FaTimes />
                    </span>
                )}
            </div>
            {showDropdown && filteredWaste.length > 0 && menuStyle && createPortal(
                <Dropdown.Menu ref={menuRef} className="waste-code-select-menu" show align="start" style={menuStyle}>
                    {Object.keys(groupedWaste).map((type) => (
                        <div key={type}>
                            <Dropdown.ItemText className="fw-bold mt-3">
                                {wasteTypes[type]}
                            </Dropdown.ItemText>
                            {groupedWaste[type].map((waste) => (
                                <Dropdown.Item
                                    key={waste._id}
                                    className="dropdown-item-search"
                                    onClick={() => handleSelect(waste)}
                                >
                                    {waste.wasteCode} - {waste.wasteName}
                                </Dropdown.Item>
                            ))}
                        </div>
                    ))}
                </Dropdown.Menu>,
                document.body
            )}
        </Form.Group>
    );
};

export default WasteCodeSelect;
